import { useState, useEffect, useCallback } from 'react';
import { API } from 'aws-amplify';
import { GET_ORG_FINDING_TYPES } from '../graphql/queries';

export interface FindingTypeTask {
  id: string;
  taskType: string;
  itemKey?: string;
  name: string;
  description?: string;
  sortOrder: number;
  zoneId?: string;
  zone?: string;
}

export interface FindingType {
  id: string;
  name: string;
  code: string;
  description?: string;
  orgId?: string;
  isSystem: boolean;
  category?: string;
  isDeleted?: boolean;
  tasks: FindingTypeTask[];
}

export default function useOrgFindingTypes() {
  const orgID = (global as any)?.me?.orgID;
  const [findingTypes, setFindingTypes] = useState<FindingType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFindingTypes = useCallback(
    async (showLoading = true) => {
      if (!orgID) return;

      try {
        if (showLoading) setIsLoading(true);
        setError(null);

        const result: any = await API.graphql({
          query: GET_ORG_FINDING_TYPES as any,
          variables: {
            request: JSON.stringify({ orgId: orgID }),
          },
        });

        const parsed = JSON.parse(result.data.getOrgFindingTypes);

        if (parsed.success) {
          setFindingTypes(parsed.data?.findingTypes ?? []);
        } else {
          setError(parsed.error ?? 'Failed to fetch finding types');
        }
      } catch (err: any) {
        console.error('Error fetching org finding types:', err);
        setError(err?.message ?? 'Failed to fetch finding types');
      } finally {
        setIsLoading(false);
      }
    },
    [orgID]
  );

  useEffect(() => {
    fetchFindingTypes();
  }, [fetchFindingTypes]);

  return {
    findingTypes,
    isLoading,
    error,
    refetch: () => fetchFindingTypes(false),
  };
}
